/**
 * PerAuthorPostTemplatesLibrary — per-author per-platform template library.
 *
 * Rebuilt on SDataTable (mirror of PlatformPostsTable pattern) so the
 * surface matches the standard project table look. Edit / Create flows
 * use the unified `PostTemplateEditDialog` instead of an inline form.
 *
 * Path: projects/{pid}/authors/{aid}/platforms/{pid}/post_templates/{id}
 *
 * Schema 1.7.0+ uses unified PostTemplate at this path (PerAuthorPostTemplate
 * legacy aggregate removed). Templates auto-seeded from system catalogue at
 * platform-add time; user can import more via "Browse catalogue" CTA, edit
 * via row click, bulk-delete via multi-select.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
  Tooltip,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import {
  SDataTable,
  type SDataTableColumnDef,
  type SDataTableRowSelectionState,
} from '@modules/ui-components';
import { usePerAuthorPostTemplates } from '../../hooks/usePerAuthorPostTemplates';
import { PerAuthorPostTemplateService } from '../../services/PerAuthorPostTemplateService';
import type { PerAuthorPostTemplate } from '../../types';
import { SystemTemplateBrowserDialog } from '../SystemTemplateBrowserDialog';
import { PostTemplateEditDialog } from '../PostTemplateEditDialog';
import type { AuthorBasePath } from '../../services/AuthorPlatformService';

const TESTID = 'per-author-post-templates-library';

export interface PerAuthorPostTemplatesLibraryProps {
  projectId: string;
  authorId: string;
  platformId: string;
  basePath?: AuthorBasePath;
}

export const PerAuthorPostTemplatesLibrary: React.FC<PerAuthorPostTemplatesLibraryProps> = ({
  projectId,
  authorId,
  platformId,
  basePath = 'authors',
}) => {
  const { templates, loading } = usePerAuthorPostTemplates(
    projectId,
    authorId,
    platformId,
    basePath,
  );
  const service = useMemo(() => new PerAuthorPostTemplateService(basePath), [basePath]);

  const [editing, setEditing] = useState<PerAuthorPostTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<SDataTableRowSelectionState>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // baseTemplateId of every imported entry — used by the browse dialog to
  // disable already-imported cards.
  const importedTemplateIds = useMemo(
    () =>
      new Set(
        templates
          .map((t) => (t as { baseTemplateId?: string }).baseTemplateId)
          .filter((id): id is string => typeof id === 'string' && id.length > 0),
      ),
    [templates],
  );

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((k) => rowSelection[k]),
    [rowSelection],
  );

  const handleSave = async (draft: Partial<PerAuthorPostTemplate>) => {
    if (editing) {
      await service.update(projectId, authorId, platformId, editing.id, draft);
    } else {
      await service.create(projectId, authorId, platformId, {
        ...draft,
        authorId,
        platformId,
      } as any);
    }
    setEditing(null);
    setCreating(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this template? This cannot be undone.')) return;
    setError(null);
    try {
      await service.delete(projectId, authorId, platformId, id);
    } catch (e: any) {
      setError(e?.message ?? 'Delete failed');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Delete ${selectedIds.length} template(s)? This cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      for (const id of selectedIds) {
        await service.delete(projectId, authorId, platformId, id);
      }
      setRowSelection({});
    } catch (e: any) {
      setError(e?.message ?? 'Bulk delete failed');
    } finally {
      setBusy(false);
    }
  };

  const handleImportFromCatalogue = async (templateIds: string[]) => {
    setBusy(true);
    setError(null);
    try {
      for (const id of templateIds) {
        await service.importFromSystemCatalogue(projectId, authorId, platformId, id);
      }
    } catch (e: any) {
      setError(e?.message ?? 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo<SDataTableColumnDef<PerAuthorPostTemplate>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 240,
        Cell: ({ row }) => {
          const t = row.original;
          const isFromCatalogue = Boolean((t as any).baseTemplateId);
          return (
            <Stack spacing={0.25}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={500}>
                  {t.name || t.id}
                </Typography>
                {isFromCatalogue && (
                  <Tooltip title="Imported from system catalogue">
                    <Chip label="catalogue" size="small" variant="outlined" sx={{ height: 18 }} />
                  </Tooltip>
                )}
              </Stack>
              {t.description && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {t.description}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        accessorKey: 'goal',
        header: 'Goal',
        size: 110,
        Cell: ({ cell }) => {
          const v = cell.getValue<string>();
          return v ? <Chip label={v} size="small" color="primary" /> : null;
        },
      },
      {
        accessorKey: 'scaffold',
        header: 'Scaffold',
        size: 460,
        enableSorting: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string>() || '';
          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: 12,
                color: 'text.secondary',
                lineHeight: 1.35,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                whiteSpace: 'normal',
                maxWidth: 440,
              }}
              title={v}
            >
              {v}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'tag',
        header: 'Tag',
        size: 110,
        Cell: ({ cell }) => {
          const v = cell.getValue<string>();
          return v ? (
            <Typography variant="caption" color="text.secondary">
              {v}
            </Typography>
          ) : null;
        },
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        enableSorting: false,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row.original.id);
            }}
            data-testid={`per-author-template-delete-${row.original.id}`}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Paper variant="outlined" sx={{ p: 3 }} data-testid={TESTID}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Templates — {platformId}</Typography>
          <Typography variant="caption" color="text.secondary">
            Reusable structural skeletons. AI fills the variables.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {selectedIds.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleBulkDelete}
              disabled={busy}
              data-testid="per-author-template-bulk-delete-btn"
            >
              Delete {selectedIds.length}
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            startIcon={<LibraryBooksIcon />}
            onClick={() => setBrowseOpen(true)}
            disabled={busy}
            data-testid="per-author-template-browse-btn"
          >
            Browse catalogue
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
            disabled={busy}
            data-testid="per-author-template-add-btn"
          >
            Add
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading templates…
        </Typography>
      ) : templates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No templates yet. Use "Browse catalogue" to import from the system pool, or "+ Add" to author one from scratch.
        </Typography>
      ) : (
        <SDataTable<PerAuthorPostTemplate>
          columns={columns}
          data={templates}
          getRowId={(row) => row.id}
          enableRowSelection
          enableSorting
          enableTopToolbar={false}
          enableBottomToolbar={templates.length > 25}
          enablePagination={templates.length > 25}
          state={{ rowSelection }}
          onRowSelectionChange={setRowSelection}
          initialState={{
            sorting: [{ id: 'name', desc: false }],
            pagination: { pageIndex: 0, pageSize: 25 },
          }}
          muiSelectCheckboxProps={{ size: 'small' }}
          muiSelectAllCheckboxProps={{ size: 'small' }}
          muiTableBodyRowProps={({ row }) => ({
            hover: true,
            onClick: () => setEditing(row.original),
            sx: { cursor: 'pointer' },
          })}
        />
      )}

      <SystemTemplateBrowserDialog
        open={browseOpen}
        onClose={() => setBrowseOpen(false)}
        platformPrefilter={platformId}
        importedTemplateIds={importedTemplateIds}
        onImport={handleImportFromCatalogue}
      />

      <PostTemplateEditDialog
        open={creating || editing !== null}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        template={editing ?? undefined}
        mode={creating ? 'create' : 'edit'}
        onSave={handleSave}
      />
    </Paper>
  );
};

export default PerAuthorPostTemplatesLibrary;
